/*
 * Avatar
 * @module components
 * @flow
 */

import React, { isValidElement } from 'react';
import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp } from 'styled-tools';
import { retinaImage } from 'polished';
import * as R from 'ramda';
import md5 from 'js-md5';

export type AvatarProps = {
  children?: React$Element<*> | string,
  palette?: string,
  src?: string,
  email?: string,
  small?: boolean,
  retina?: boolean,
};

type CasePropType = 'toUpper' | 'toLower';

const SIZES = {
  SMALL: 20,
  ORDINARY: 60,
};

const stringToCase = (
  value: string,
  toCase: CasePropType = 'toUpper',
): string =>
  R.pipe(
    R.trim,
    toCase === 'toUpper' ? R.toUpper : R.toLower,
  )(value);
const gravatarUrl = (email: string, size: number): string =>
  `https://www.gravatar.com/avatar/${md5(
    stringToCase(email, 'toLower'),
  )}?default=retro&s=${size}`;
const getInitials = R.pipe(
  stringToCase,
  R.split(' '),
  R.take(2),
  R.map(R.head),
  R.join(''),
);
const foregroundColor = ({ palette: p }: AvatarProps) =>
  palette('grayscale', 0, !R.equals('secondary', p));
const backgroundImage = ({ retina, src, email, small }: AvatarProps) => {
  if (!src && !email) {
    return css`
      background-color: ${palette(1)};
      color: ${palette('gray', 0, true)};
    `;
  }

  const source =
    src || gravatarUrl(email || '', SIZES[small ? 'SMALL' : 'ORDINARY']);

  if (retina) {
    return retinaImage(source, 'contain');
  }

  return css`
    background: 50% 50% no-repeat transparent;
    background-size: contain;
    background-image: url(${source});
    text-indent: -1000%;
    white-space: nowrap;
  `;
};

const styles = css`
  display: inline-flex;
  width: ${ifProp('small', `${SIZES.SMALL}px`, `${SIZES.ORDINARY}px`)};
  height: ${ifProp('small', `${SIZES.SMALL}px`, `${SIZES.ORDINARY}px`)};
  font-size: ${ifProp('small', '14px', '24px')};
  color: ${foregroundColor};
  align-items: center;
  justify-content: center;
  overflow: hidden;
  user-select: none;
  ${backgroundImage};
`;

const AvatarWrapper = styled.div`
  ${styles};
`;

/**
 * Avatar React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - childs
 * @param {string} [props.src] - link to the image
 * @param {string} [props.email] - gravatar email address
 * @param {string} [props.palette='primary'] - current QR Code palette
 * @example <caption>Simple usage</caption>
 * <Avatar src="http://i.pravatar.cc/150?img=47" />
 * @returns {React.Node<*>} React-component
 */
export const Avatar = ({ children, ...props }: AvatarProps) => {
  let content = null;

  if (children) {
    // $FlowIssue
    content = isValidElement(children) ? children : getInitials(`${children}`);
  }

  return (
    <AvatarWrapper testid="avatar" {...props}>
      {content}
    </AvatarWrapper>
  );
};

Avatar.defaultProps = {
  palette: 'primary',
};
